import { Injectable, Signal, WritableSignal, computed, signal } from '@angular/core';
import { PropertyDefinition } from '@shared/models/property-schema';
import { openApiSchemaToProperties, propertiesToOpenApiSchema } from '@shared/utils/openapi-schema-utils';

/** Scope — сущность, для которой директивы `VariableTrackerDirective` мержат
 *  переменные в одну общую OpenAPI-схему. Все source'ы одного scope видят
 *  одинаковую результирующую схему; если var исчезла из ВСЕХ текстов scope,
 *  она удаляется из схемы (пользовательский intent: правлю текст — переменные
 *  подгоняются). Метаданные (`title`/`type`/`fallback`/`nullable`) при этом
 *  сохраняются пока var существует хотя бы в одном тексте scope. */
interface ScopeState {
  /** {sourceId -> extracted var names для текущего текста этого источника}. */
  sources: WritableSignal<Map<string, string[]>>;
  /** {name -> full PropertyDefinition}. Правки drawer'ов и seed от parent'а
   *  живут здесь; при рекомпуте схемы забираем ТОЛЬКО те, что упомянуты
   *  в текстах, но всё, что здесь лежит — «истина» по метаданным. */
  meta: WritableSignal<Map<string, PropertyDefinition>>;
  /** Готовая схема — computed из sources ∪ meta. */
  schema: Signal<any>;
  /** Флаг того, что мы уже подстроились под initialSchema первого источника
   *  (чтобы последующие регистрации не перетирали чужие правки). */
  seeded: boolean;
}

export interface DynamicVariable {
  name: string;
  description: string;
  type: 'string' | 'number' | 'boolean' | 'integer';
  fallback_value: string;
  nullable: boolean;
  usedInInputs: string[];
}

export interface VariableSource {
  sourceId: string;
  sourceLabel: string;
  variables: string[];
}

@Injectable({
  providedIn: 'root'
})
export class DynamicVariablesService {
  private initialVairales: DynamicVariable[] = [];
  // Справочник источников переменных
  private variableSources: Map<string, VariableSource> = new Map();
  /**
   * Объявляем те переменные, с которыми будем сопоставлять изменения
   * @param variables 
   */
  initialiseVariables(variables: DynamicVariable[]) {
    this.initialVairales = variables;
  }
  /**
   * Extract variables from text using {{variable}} syntax
   */
  extractVariablesFromText(text: string): string[] {
    const variableRegex = /\{\{([^}]+)\}\}/g;
    const variables: string[] = [];
    let match;
    
    while ((match = variableRegex.exec(text)) !== null) {
      const variableName = match[1].trim();
      if (variableName && !variables.includes(variableName)) {
        variables.push(variableName);
      }
    }
    
    return variables;
  }

  /**
   * Insert variable at specified position in text
   */
  insertVariableAtPosition(text: string, variableName: string, position: number = -1): string {
    const variableText = `{{${variableName}}}`;
    
    // If position not specified, append to end
    if (position === -1) {
      return text + variableText;
    }
    
    // Insert at specified position
    return text.slice(0, position) + variableText + text.slice(position);
  }

  /**
   * Add variable to the end of text
   */
  addVariableToText(text: string, variableName: string): string {
    const variableText = `{{${variableName}}}`;
    return text + variableText;
  }

  /**
   * Create or update dynamic variable in collection
   */
  upsertVariable(variables: DynamicVariable[], name: string, updates?: Partial<DynamicVariable>): DynamicVariable[] {
    const existingIndex = variables.findIndex(v => v.name === name);
    
    if (existingIndex >= 0) {
      // Update existing variable
      variables[existingIndex] = { ...variables[existingIndex], ...updates };
    } else {
      // Create new variable
      const existingInitialVariable = this.initialVairales.find(x=>x.name == name);
      const newVariable: DynamicVariable = existingInitialVariable ?? {
        name,
        description: `Parameter: ${name}`,
        type: 'string',
        fallback_value: '',
        nullable: true,
        usedInInputs: [],
        ...updates
      };
      variables.push(newVariable);
    }
    
    return [...variables];
  }

  /**
   * Remove variables that are no longer used
   */
  cleanUnusedVariables(variables: DynamicVariable[], usedVariableNames: string[]): DynamicVariable[] {
    return variables.filter(variable => usedVariableNames.includes(variable.name));
  }

  /**
   * Update variable usage information
   */
  updateVariableUsage(variables: DynamicVariable[], variableName: string, inputLabel: string, isUsed: boolean): DynamicVariable[] {
    const variable = variables.find(v => v.name === variableName);
    if (!variable) return variables;

    if (isUsed && !variable.usedInInputs.includes(inputLabel)) {
      variable.usedInInputs.push(inputLabel);
    } else if (!isUsed) {
      variable.usedInInputs = variable.usedInInputs.filter(label => label !== inputLabel);
    }

    return [...variables];
  }

  /**
   * Get variables used in specific input
   */
  getVariablesForInput(allVariables: DynamicVariable[], inputText: string): DynamicVariable[] {
    const variableNames = this.extractVariablesFromText(inputText);
    return allVariables.filter(variable => variableNames.includes(variable.name));
  }

  /**
   * Update variables for a specific source
   */
  updateSourceVariables(sourceId: string, sourceLabel: string, variables: string[]): void {
    this.variableSources.set(sourceId, {
      sourceId,
      sourceLabel,
      variables: [...variables]
    });
  }

  /**
   * Remove source from tracking
   */
  removeSource(sourceId: string): void {
    this.variableSources.delete(sourceId);
  }

  /**
   * Get all unique variables from all sources
   */
  getAllVariableNames(): string[] {
    const allVariables = new Set<string>();
    
    this.variableSources.forEach(source => {
      source.variables.forEach(variable => allVariables.add(variable));
    });
    
    return Array.from(allVariables);
  }

  /**
   * Get usage information for a variable (which sources use it)
   */
  getVariableUsage(variableName: string): string[] {
    const uniqueLabels = new Set<string>();
    
    this.variableSources.forEach(source => {
      if (source.variables.includes(variableName)) {
        uniqueLabels.add(source.sourceLabel);
      }
    });
    
    return Array.from(uniqueLabels);
  }

  /**
   * Build complete DynamicVariable array with usage information
   */
  buildVariablesWithUsage(existingVariables: DynamicVariable[] = []): DynamicVariable[] {
    const allVariableNames = this.getAllVariableNames();
    const result: DynamicVariable[] = [];

    // Add/update variables
    allVariableNames.forEach(variableName => {
      const existingVar = existingVariables.find(v => v.name === variableName);
      const usedInInputs = this.getVariableUsage(variableName);

      if (existingVar) {
        // Update existing variable with new usage info
        result.push({
          ...existingVar,
          usedInInputs
        });
      } else {
        const existingInitialVariable = this.initialVairales.find(x=>x.name == variableName);
        if (existingInitialVariable) {
          existingInitialVariable.usedInInputs = usedInInputs;
        }
        // Create new variable
        result.push(existingInitialVariable ?? {
          name: variableName,
          description: `Variable: ${variableName}`,
          type: 'string',
          fallback_value: '',
          nullable: true,
          usedInInputs
        });
      }
    });

    // Keep existing variables that are still used
    return result.filter(variable => allVariableNames.includes(variable.name));
  }


  /**
   * Clear all sources (useful for component cleanup)
   */
  clearAllSources(): void {
    this.variableSources.clear();
  }

  /**
   * Update variables from text and existing OpenAPI schema
   * Returns PropertyDefinitions for all variables found in text or existing schema
   */
  updateVariablesFromText(text: string, existingSchema?: any): PropertyDefinition[] {
    // Load existing properties from schema if available
    let existingProperties: PropertyDefinition[] = [];
    if (existingSchema) {
      existingProperties = openApiSchemaToProperties(existingSchema);
    }

    // If no text, return all existing schema properties
    if (!text) {
      return existingProperties;
    }

    // Extract variable names from text
    const extractedVarNames = this.extractVariablesFromText(text);

    // Start with all existing schema properties
    const result: PropertyDefinition[] = [...existingProperties];
    const existingNames = new Set(existingProperties.map(p => p.name));

    // Add new variables found in text that don't exist in schema
    extractedVarNames.forEach(varName => {
      if (!existingNames.has(varName)) {
        // Check if there's an initial variable with this name
        const initialVariable = this.initialVairales.find(v => v.name === varName);

        if (initialVariable) {
          // Convert DynamicVariable to PropertyDefinition
          result.push({
            name: initialVariable.name,
            title: initialVariable.name,
            description: initialVariable.description,
            type: initialVariable.type,
            nullable: initialVariable.nullable,
            default: initialVariable.nullable ? null : this.getDefaultForType(initialVariable.type),
            isDefaultNull: initialVariable.nullable
          });
        } else {
          // Create new variable with default configuration
          result.push({
            name: varName,
            title: varName,
            description: `Variable: ${varName}`,
            type: 'string',
            nullable: true,
            default: null,
            isDefaultNull: true
          });
        }
      }
    });

    return result;
  }

  /**
   * Get default value for a PropertyDefinition type
   */
  private getDefaultForType(type: PropertyDefinition['type']): any {
    switch (type) {
      case 'string':
        return '';
      case 'integer':
      case 'number':
        return 0;
      case 'boolean':
        return false;
      default:
        return null;
    }
  }

  /**
   * Convert PropertyDefinitions to OpenAPI schema
   */
  propertiesToSchema(properties: PropertyDefinition[]): any {
    return propertiesToOpenApiSchema(properties);
  }

  /**
   * Convert OpenAPI schema to PropertyDefinitions
   */
  schemaToProperties(schema: any): PropertyDefinition[] {
    return openApiSchemaToProperties(schema);
  }

  // -----------------------------------------------------------------
  // Scoped API — используется VariableTrackerDirective.
  // Задача: несколько директив с одинаковым scope (например 'agent')
  // делят одну схему. При изменении текста в любой директиве var'ы
  // добавляются/удаляются, метаданные сохраняются.
  // -----------------------------------------------------------------

  private scopes: Map<string, ScopeState> = new Map();

  private getOrCreateScope(scope: string): ScopeState {
    let st = this.scopes.get(scope);
    if (st) return st;

    const sources = signal(new Map<string, string[]>());
    const meta = signal(new Map<string, PropertyDefinition>());
    const schema = computed(() => {
      const allNames = new Set<string>();
      for (const vars of sources().values()) for (const v of vars) allNames.add(v);
      const m = meta();
      const props: PropertyDefinition[] = [];
      for (const name of allNames) {
        props.push(m.get(name) ?? {
          name,
          title: name,
          description: `Variable: ${name}`,
          type: 'string',
          nullable: true,
          default: null,
          isDefaultNull: true,
        });
      }
      return propertiesToOpenApiSchema(props);
    });

    st = { sources, meta, schema, seeded: false };
    this.scopes.set(scope, st);
    return st;
  }

  /** Регистрирует source в scope. Если scope ещё не seed'ился и передан
   *  initialSchema — забирает из неё все PropertyDefinition в meta (первый
   *  wins). Возвращает read-only schema-сигнал для подписки. */
  registerScopeSource(scope: string, sourceId: string, initialSchema?: any): Signal<any> {
    const st = this.getOrCreateScope(scope);
    if (!st.seeded && initialSchema) {
      const props = openApiSchemaToProperties(initialSchema);
      st.meta.set(new Map(props.map(p => [p.name, p])));
      st.seeded = true;
    }
    const next = new Map(st.sources());
    if (!next.has(sourceId)) {
      next.set(sourceId, []);
      st.sources.set(next);
    }
    return st.schema;
  }

  /** Обновляет список var'ов, извлечённых из текста этого source'а.
   *  System-переменные (`system__*`) отфильтровываются — они разрешаются
   *  бэком по стандартным именам, юзер их не редактирует, в OpenAPI-схему
   *  не идут (иначе в drawer'ах вылезли бы редактируемые «системные»
   *  переменные, что противоречит их смыслу). Схема пересчитывается
   *  автоматически (computed). */
  setScopeSourceText(scope: string, sourceId: string, text: string): void {
    const st = this.getOrCreateScope(scope);
    const names = this.extractVariablesFromText(text ?? '').filter(n => !n.startsWith('system__'));
    const cur = st.sources().get(sourceId);
    if (cur && cur.length === names.length && cur.every((n, i) => n === names[i])) return;
    const next = new Map(st.sources());
    next.set(sourceId, names);
    st.sources.set(next);
  }

  /** Применяет новую схему к scope (drawer editing / parent-side push).
   *  Затирает meta целиком — но т.к. schema computed берёт из meta только
   *  var'ы, встречающиеся в текстах, orphan-метаданные хранятся до тех
   *  пор пока хоть один текст их использует. */
  setScopeSchema(scope: string, schema: any): void {
    const st = this.getOrCreateScope(scope);
    const props = schema ? openApiSchemaToProperties(schema) : [];
    st.meta.set(new Map(props.map(p => [p.name, p])));
    st.seeded = true;
  }

  /** Снимает регистрацию source. Если это был последний — scope не сносим,
   *  meta может понадобиться после ре-монта того же контрола. */
  unregisterScopeSource(scope: string, sourceId: string): void {
    const st = this.scopes.get(scope);
    if (!st) return;
    if (!st.sources().has(sourceId)) return;
    const next = new Map(st.sources());
    next.delete(sourceId);
    st.sources.set(next);
  }

  /** Полный сброс scope — используется когда parent-компонент размонтируется. */
  clearScope(scope: string): void {
    this.scopes.delete(scope);
  }
}